/**
 * PDS Home - Client-side hydration
 * 
 * This module adds interactivity to the static HTML generated by 11ty.
 * It handles:
 * - Omnibox search and section navigation
 * - Preset switcher
 * - Form schema binding and submission
 * - Terminal typing animation
 * - Scroll behaviors
 */

import { html } from "#pds/lit";
import { PDS } from "@pure-ds/core";
import {
  isPresetThemeCompatible,
  normalizePresetThemes,
  resolveThemePreference,
} from "./pds-theme-utils";

// Form schema (kept in JS since it needs dynamic binding)
const FORM_SCHEMA = {
  type: "object",
  required: ["identity", "project", "preferences"],
  properties: {
    identity: {
      type: "object",
      title: "Identity",
      required: ["fullName", "email", "role"],
      properties: {
        fullName: {
          type: "string",
          title: "Full name",
          minLength: 2,
          examples: ["Avery Morgan"],
        },
        email: {
          type: "string",
          format: "email",
          title: "Work email",
          examples: ["avery@studio.com"],
        },
        company: {
          type: "string",
          title: "Company",
          examples: ["Pulse Labs"],
        },
        role: {
          type: "string",
          title: "Role",
          examples: ["Product Lead"],
        },
      },
    },
    project: {
      type: "object",
      title: "Project",
      required: ["projectType", "goals"],
      properties: {
        projectType: {
          type: "string",
          title: "Project focus",
          oneOf: [
            { const: "web", title: "Web app" },
            { const: "mobile", title: "Mobile app" },
            { const: "system", title: "Design system" },
            { const: "brand", title: "Brand system" },
            { const: "other", title: "Other..." },
          ],
        },
        otherProject: {
          type: "string",
          maxLength: 512,
          title: "Tell us more",
          examples: ["Creator marketplace with a premium tier"],
        },
        goals: {
          type: "string",
          maxLength: 512,
          examples: ["Unify brand, launch new product UI, and ship faster"],
        },
      },
    },
    content: {
      type: "object",
      title: "Content",
      properties: {
        notes: {
          type: "string",
          title: "Content notes",
          examples: ["Include multi-brand support and internationalization"],
        },
        assets: {
          type: "string",
          title: "Upload brand assets",
        },
      },
    },
    preferences: {
      type: "object",
      title: "Preferences",
      required: ["acceptTerms"],
      properties: {
        updates: {
          type: "boolean",
          title: "Send me product updates",
        },
        darkPreview: {
          type: "boolean",
          title: "Preview dark mode by default",
        },
        acceptTerms: {
          type: "boolean",
          title: "I agree to the terms",
        },
      },
    },
  },
};

const FORM_UI_SCHEMA = {
  "/identity": {
    "ui:layout": "grid",
    "ui:layoutOptions": { columns: "auto", autoSize: "md", gap: "md" },
  },
  "/identity/fullName": {
    "ui:icon": "user",
    "ui:autocomplete": "name",
  },
  "/identity/email": {
    "ui:icon": "envelope",
    "ui:autocomplete": "email",
  },
  "/identity/company": { "ui:icon": "briefcase" },
  "/identity/role": { "ui:widget": "role-omnibox" },
  "/project": {
    "ui:layout": "grid",
    "ui:layoutOptions": { columns: "auto", autoSize: "md", gap: "md" },
  },
  "/project/projectType": { "ui:class": "buttons" },
  "/project/otherProject": {
    "ui:visibleWhen": { "/project/projectType": "other" },
    "ui:requiredWhen": { "/project/projectType": "other" },
  },
  "/project/goals": { "ui:widget": "textarea", "ui:rows": 3 },
  "/content": {
    "ui:layout": "grid",
    "ui:layoutOptions": { columns: "auto", autoSize: "md", gap: "md" },
  },
  "/content/notes": { "ui:widget": "richtext" },
  "/content/assets": { "ui:widget": "upload", "ui:icon": "upload" },
  "/preferences": {
    "ui:layout": "grid",
    "ui:layoutOptions": { columns: "auto", autoSize: "md", gap: "md" },
  },
  "/preferences/updates": {},
  "/preferences/darkPreview": {},
  "/preferences/acceptTerms": {},
};

const FORM_OPTIONS = {
  widgets: {
    booleans: "toggle-with-icons",
  },
  enhancements: {
    icons: true,
    datalists: true,
    rangeOutput: true,
  },
};

const ROLE_OPTIONS = [
  "Product Designer",
  "UX Designer",
  "UI Designer",
  "Design Systems Lead",
  "Creative Director",
  "Frontend Engineer",
  "Full-stack Engineer",
  "Design Engineer",
  "Product Manager",
  "Founder",
];

/**
 * Initialize all home page hydration
 */
export async function initHomeHydration() {
  const state = {
    sections: [],
    omniboxSettings: null,
    roleOmniboxSettings: null,
    presetOmniboxSettings: null,
    handleOmniboxScroll: null,
    omniboxScrollRaf: null,
    themeChangeHandler: null,
    gettingStartedTyped: false,
    cancelGettingStartedTyping: false,
    gettingStartedObserver: null,
    gettingStartedVisibilityHandler: null,
    gettingStartedFocusHandler: null,
    gettingStartedTypingToken: 0,
    gettingStartedTypingInProgress: false,
    gettingStartedTypingTimer: null,
  };

  // Build settings
  state.omniboxSettings = buildOmniboxSettings(state);
  state.roleOmniboxSettings = buildRoleOmniboxSettings(state);
  state.presetOmniboxSettings = buildPresetOmniboxSettings();

  // Setup all features
  setupSections(state);
  setupOmnibox(state);
  setupPresetOmnibox(state);
  await setupForm(state);
  setupGettingStartedTerminal(state);
  setupOmniboxAutoHide(state);
  setupGettingStartedTriggers(state);
  setupScrollButtons(state);

  // Listen for theme changes
  state.themeChangeHandler = () => {
    state.presetOmniboxSettings = buildPresetOmniboxSettings();
    setupPresetOmnibox(state);
  };
  PDS.addEventListener("pds:theme:changed", state.themeChangeHandler);

  return state;
}

function setupSections(state) {
  const sections = Array.from(
    document.querySelectorAll("[data-home-section]")
  ).map((section) => {
    const title = section.querySelector("h1, h2")?.textContent?.trim() || "";
    return { id: section.getAttribute("data-home-section"), title };
  });
  state.sections = sections.filter((section) => section.id);
}

function buildOmniboxSettings(state) {
  return {
    iconHandler: (item) => {
      return item.icon ? `<pds-icon icon="${item.icon}"></pds-icon>` : null;
    },
    categories: {
      Highlights: {
        trigger: (options) => options.search.length === 0,
        action: (options) => scrollToSection(options.id),
        getItems: () =>
          (state.sections || []).map((section) => ({
            text: section.title || section.id,
            id: section.id,
            icon: "folder-simple",
          })),
      },
      Query: {
        trigger: (options) => options.search.length >= 2,
        action: (options) => handleOmniboxSelect(options),
        getItems: async (options) => {
          const query = (options.search || "").trim();
          if (!query) return [];
          try {
            const results = await PDS.query(query);
            return (results || []).map((result) => ({
              text: result.text,
              id: result.value,
              icon: result.icon || "magnifying-glass",
              category: result.category,
              code: result.code,
            }));
          } catch (error) {
            console.warn("Omnibox query failed:", error);
            return [];
          }
        },
      },
    },
  };
}

function buildRoleOmniboxSettings(state) {
  return {
    hideCategory: true,
    iconHandler: (item) => {
      return item.icon ? `<pds-icon icon="${item.icon}"></pds-icon>` : "";
    },
    categories: {
      Roles: {
        trigger: (options) => options.search.length === 0,
        getItems: () =>
          ROLE_OPTIONS.map((role) => ({
            text: role,
            id: role,
            icon: "",
          })),
        action: (options) => {
          applyOmniboxSelection(options);
          return options?.id;
        },
      },
      Search: {
        trigger: (options) => options.search.length > 0,
        getItems: (options) => {
          const query = (options.search || "").trim().toLowerCase();
          if (!query) return [];
          return ROLE_OPTIONS.filter((role) =>
            role.toLowerCase().includes(query)
          ).map((role) => ({
            text: role,
            id: role,
            icon: "",
          }));
        },
        action: (options) => {
          applyOmniboxSelection(options);
          return options?.id;
        },
      },
    },
  };
}

function applyOmniboxSelection(options) {
  const text = options?.text || options?.id || "";
  if (!text) return;

  const active = document.activeElement;
  let omnibox = null;

  if (active?.tagName === "PDS-OMNIBOX") {
    omnibox = active;
  } else if (active?.tagName === "INPUT") {
    const host = active.getRootNode?.().host;
    if (host?.tagName === "PDS-OMNIBOX") {
      omnibox = host;
    }
  }

  if (!omnibox) {
    omnibox = document.querySelector("#pds-home-form pds-omnibox");
  }

  if (omnibox) {
    omnibox.value = text;
  }
}

async function handleOmniboxSelect(options) {
  if (options?.code && navigator.clipboard) {
    await navigator.clipboard.writeText(options.code);
    await PDS.toast("Copied token to clipboard", { type: "success" });
  } else if (options?.id) {
    scrollToSection(options.id);
  }
}

function scrollToSection(sectionId) {
  const section = document.querySelector(`[data-home-section="${sectionId}"]`);
  if (!section) return;
  section.scrollIntoView({ behavior: "smooth", block: "start" });
  section.classList.add("is-highlight");
  window.setTimeout(() => {
    section.classList.remove("is-highlight");
  }, 1200);
}

function setupOmnibox(state) {
  const omnibox = document.querySelector("#pds-home-omnibox");
  if (!omnibox) return;
  customElements.whenDefined("pds-omnibox").then(() => {
    omnibox.settings = state.omniboxSettings;
  });
}

function setupPresetOmnibox(state) {
  const omnibox = document.querySelector("#pds-home-preset-omnibox");
  if (!omnibox) return;
  customElements.whenDefined("pds-omnibox").then(() => {
    omnibox.settings = state.presetOmniboxSettings;
  });
}

function buildPresetOmniboxSettings() {
  const resolvedTheme = resolveThemePreference(PDS.theme);
  return {
    hideCategory: true,
    iconHandler: (item) => {
      const preset = PDS.presets[item.id];
      return /*html*/ `<span style="display: flex; gap: 1px; flex-shrink: 0;">
        <span style="display: inline-block; width: 10px; height: 20px; background-color: ${preset.colors.primary}"></span>
        <span style="display: inline-block; width: 10px; height: 20px; background-color: ${preset.colors.secondary}"></span>
        <span style="display: inline-block; width: 10px; height: 20px; background-color: ${preset.colors.accent}"></span>
      </span>`;
    },
    categories: {
      Presets: {
        trigger: (options) => true,
        action: (options) => {
          if (options?.disabled) return options?.id;
          return applyPresetSelection(options);
        },
        getItems: (options) => {
          const all = Object.values(PDS.presets || {});
          const query = (options.search || "").toLowerCase();

          const filtered = all.filter((preset) => {
            if (!query) return true;
            const name = preset.name?.toLowerCase() || "";
            const description = preset.description?.toLowerCase() || "";
            return name.includes(query) || description.includes(query);
          });

          return filtered.map((preset) => {
            const supportedThemes = normalizePresetThemes(preset);
            const isCompatible = isPresetThemeCompatible(preset, resolvedTheme);
            const themeHint =
              supportedThemes.length === 1
                ? `${supportedThemes[0]} only`
                : `Not available in ${resolvedTheme} mode`;
            const description = preset.description || "";
            const displayDescription = isCompatible
              ? description
              : description
                ? `${description} - ${themeHint}`
                : themeHint;

            return {
              id: preset.id,
              text: preset.name,
              description: displayDescription,
              icon: "palette",
              class: isCompatible ? "" : "disabled",
              disabled: !isCompatible,
              tooltip: isCompatible ? "" : themeHint,
            };
          });
        },
      },
    },
  };
}

function applyPresetSelection(options) {
  if (options?.disabled) return;
  const preset = PDS.presets?.[options?.id];
  if (!preset) return;

  PDS.applyLivePreset(preset.id);

  if (PDS.toast) {
    PDS.toast(`Preset selected: ${preset.name}`, { type: "info" });
  }
}

function setupOmniboxAutoHide(state) {
  const omnibox = document.querySelector("#pds-home-omnibox");
  if (!omnibox) return;

  let lastScrollY = window.scrollY || 0;
  state.handleOmniboxScroll = () => {
    if (state.omniboxScrollRaf) return;
    state.omniboxScrollRaf = window.requestAnimationFrame(() => {
      const currentScrollY = window.scrollY || 0;

      if (currentScrollY > lastScrollY) {
        omnibox.classList.add("is-offscreen");
      } else if (currentScrollY < lastScrollY) {
        omnibox.classList.remove("is-offscreen");
      }

      if (currentScrollY === 0) {
        omnibox.classList.remove("is-offscreen");
      }

      lastScrollY = currentScrollY;
      state.omniboxScrollRaf = null;
    });
  };

  window.addEventListener("scroll", state.handleOmniboxScroll, {
    passive: true,
  });
}

async function setupForm(state) {
  const form = document.querySelector("#pds-home-form");
  if (!form) return;

  await customElements.whenDefined("pds-form");

  form.jsonSchema = FORM_SCHEMA;
  form.uiSchema = FORM_UI_SCHEMA;
  form.options = FORM_OPTIONS;

  form.defineRenderer(
    "omnibox",
    ({ id, path, value, attrs, set }) => html`
      <pds-omnibox
        id=${id}
        name=${path}
        placeholder=${attrs?.placeholder || "Search PDS..."}
        .value=${value ?? ""}
        .settings=${state.omniboxSettings}
        @input=${(event) => set(event.target.value)}
      ></pds-omnibox>
    `
  );

  form.defineRenderer(
    "role-omnibox",
    ({ id, path, value, attrs, set }) => html`
      <pds-omnibox
        item-grid="0 1fr 0"
        id=${id}
        name=${path}
        placeholder=${attrs?.placeholder || "Select a role"}
        .value=${value ?? ""}
        .settings=${state.roleOmniboxSettings}
        @input=${(event) => set(event.target.value)}
      ></pds-omnibox>
    `
  );

  form.addEventListener("pw:submit", async (event) => {
    const submitBtn = form.querySelector('button[type="submit"]');
    submitBtn?.classList.add("btn-working");
    try {
      await new Promise((resolve) => setTimeout(resolve, 2200));
      console.log("PDS Home form submitted:", event.detail?.json);
      await PDS.toast("Request received! We will reach out shortly.", {
        type: "success",
      });
      form.reset();
    } catch (error) {
      await PDS.toast(`Submission failed: ${error.message}`, {
        type: "error",
      });
    } finally {
      submitBtn?.classList.remove("btn-working");
    }
  });
}

function setupGettingStartedTerminal(state) {
  if (state.gettingStartedTyped) return;
  const terminal = document.querySelector("[data-terminal-code]");
  if (!terminal) return;

  const typed = terminal.querySelector("[data-terminal-typed]");
  const command = terminal.getAttribute("data-command") || "";
  if (!typed || !command) return;

  state.gettingStartedTyped = true;
  restartGettingStartedTyping(state, typed, command);
}

function setupGettingStartedTriggers(state) {
  const terminal = document.querySelector("[data-terminal-code]");
  if (!terminal) return;

  const typed = terminal.querySelector("[data-terminal-typed]");
  const command = terminal.getAttribute("data-command") || "";
  if (!typed || !command) return;

  if (!state.gettingStartedObserver) {
    state.gettingStartedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            restartGettingStartedTyping(state, typed, command);
          }
        });
      },
      { threshold: 0.08 }
    );
    state.gettingStartedObserver.observe(terminal);
  }

  if (!state.gettingStartedVisibilityHandler) {
    state.gettingStartedVisibilityHandler = () => {
      if (!document.hidden) {
        restartGettingStartedTyping(state, typed, command);
      }
    };
    document.addEventListener(
      "visibilitychange",
      state.gettingStartedVisibilityHandler
    );
  }

  if (!state.gettingStartedFocusHandler) {
    state.gettingStartedFocusHandler = () => {
      restartGettingStartedTyping(state, typed, command);
    };
    window.addEventListener("focus", state.gettingStartedFocusHandler);
  }
}

function restartGettingStartedTyping(state, target, text) {
  const token = ++state.gettingStartedTypingToken;
  target.textContent = "";
  state.cancelGettingStartedTyping = false;
  state.gettingStartedTypingInProgress = true;
  clearGettingStartedTimer(state);

  const stream = buildTerminalCharStream(text);

  const initialDelay = 140 + Math.random() * 220;
  state.gettingStartedTypingTimer = window.setTimeout(() => {
    if (state.cancelGettingStartedTyping) return;
    if (token !== state.gettingStartedTypingToken) return;

    typeText(state, target, stream, token, 0);
  }, initialDelay);
}

function typeText(state, target, stream, token, index) {
  if (state.cancelGettingStartedTyping) return;
  if (token !== state.gettingStartedTypingToken) return;

  if (index >= stream.length) {
    state.gettingStartedTypingInProgress = false;
    return;
  }

  const { char, className } = stream[index];
  window.requestAnimationFrame(() => {
    if (state.cancelGettingStartedTyping) return;
    if (token !== state.gettingStartedTypingToken) return;

    if (className) {
      const span = document.createElement("span");
      span.className = className;
      span.textContent = char;
      target.appendChild(span);
    } else {
      target.appendChild(document.createTextNode(char));
    }
    const delay = getHumanTypingDelay(char);

    state.gettingStartedTypingTimer = window.setTimeout(() => {
      typeText(state, target, stream, token, index + 1);
    }, delay);
  });
}

function clearGettingStartedTimer(state) {
  if (state.gettingStartedTypingTimer) {
    window.clearTimeout(state.gettingStartedTypingTimer);
    state.gettingStartedTypingTimer = null;
  }
}

function getHumanTypingDelay(char) {
  const base = 50 + Math.random() * 90;
  const pauseChars = new Set([" ", "/", "-", "@", "."]);
  const extraPause = pauseChars.has(char) ? 120 + Math.random() * 170 : 0;
  const occasionalPause =
    Math.random() < 0.12 ? 160 + Math.random() * 160 : 0;
  return Math.round(base + extraPause + occasionalPause);
}

function buildTerminalCharStream(text) {
  const match = text.match(/^(\S+)(\s+)(\S+)(\s+)(.+)$/);
  if (!match) {
    return Array.from(text).map((char) => ({ char, className: "" }));
  }

  const [, cmd, gap1, action, gap2, rest] = match;
  const segments = [
    { text: cmd, className: "terminal-token terminal-token-cmd" },
    { text: gap1, className: "" },
    { text: action, className: "terminal-token terminal-token-action" },
    { text: gap2, className: "" },
    { text: rest, className: "terminal-token terminal-token-arg" },
  ];

  return segments.flatMap((segment) =>
    Array.from(segment.text).map((char) => ({
      char,
      className: segment.className,
    }))
  );
}

function setupScrollButtons(state) {
  // Handle scroll buttons with data-scroll-target attribute
  document.querySelectorAll("[data-scroll-target]").forEach((button) => {
    button.addEventListener("click", () => {
      const target = button.getAttribute("data-scroll-target");
      if (target) {
        scrollToSection(target);
      }
    });
  });
}
